import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, CircularProgress, IconButton, Box } from "@mui/material";
import AttendeeCard from "../MixerEvent/components/AttendeeCard";
import AttendeePage from "../RoundComponents/AttendeePage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import request from "../../utilities/requests";

import { toast } from "../../utilities/SnackBarUtils";

const GroupEvent = ({eventId}) => {
  const navigate = useNavigate();
  const [pairingEvent, setPairingEvent] = useState(null);
  const [round, setRound] = useState(null);
  const [roundData, setRoundData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [canMoveForward, setCanMoveForward] = useState(false);
  const [canMoveBack, setCanMoveBack] = useState(false);
  const [onBreak, setOnBreak] = useState(false);

  useEffect(() => {
    if (!pairingEvent) {
      setLoading(true);
      getPairingEvent();
    }
  }, []);

  useEffect(() => {
    if (round === 1) {
      setCanMoveBack(false);
    } else {
      setCanMoveBack(true);
    }

    if (round === pairingEvent?.numberOfRounds) {
      setCanMoveForward(false);
    } else {
      setCanMoveForward(true);
    }
  }, [round]);

  const getPairingEvent = async () => {
    const result = await request(`pairingevent/activeevent?eventId=${eventId}`);
    if (!result.success) {
      toast.error(result.error);
      navigate("/");
    } else if (result.success && !result.pairingEvent) {
      toast.warning("No active events. Heading back to events page");
      navigate("/");
    }
    setPairingEvent(result.pairingEvent);
    setRound(result.pairingEvent.currentRound);
    getRound(result.pairingEvent._id, result.pairingEvent.currentRound);
  };

  const getRound = async (pairingEventId, round) => {
    if (!roundData) setLoading(true);
    const result = await request(
      `pairingevent/groupround?pairingEventId=${pairingEventId}&round=${round}`
    );
    if (result.success) {
      if (result.break) {
        setOnBreak(true);
        setRound(result.round);
        setRoundData(null);
      } else {
        setOnBreak(false);
        setRoundData(result.data);
        setRound(result.data.round);
      }
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleSelectAttendee = (attendee) => {
    setSelectedAttendee(attendee);
    setDialogOpen(true);
  };

  const handleClickMove = async (btn) => {
    let newRound;
    if (btn === "next") {
      newRound = round + 1;
    } else if (btn === "back") {
      newRound = round - 1;
    }
    setLoading(true);
    await getRound(pairingEvent._id, newRound);
    setLoading(false);
  };

  const handleClose = () => {
    getRound(pairingEvent._id, round);
    setDialogOpen(false);
  };

  if (selectedAttendee) {
    return (
      <AttendeePage
        attendee={selectedAttendee}
        open={dialogOpen}
        close={handleClose}
        onExited={() => setSelectedAttendee(null)}
      />
    );
  }

  const renderAttendees = () => {
    if (roundData) {
      return (
        <Grid container spacing={1.5} sx={{ p: 1 }}>
          {roundData?.users.map((attendee) => {
            return (
              <AttendeeCard
                key={attendee?._id}
                attendee={attendee}
                handleSelect={handleSelectAttendee}
                xs={6}
              />
            );
          })}
        </Grid>
      );
    }
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={100} />
      </div>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {onBreak ? (
          <Box sx={{ pt: 6, px: 2, textAlign: "center" }}>
            <Typography variant="h4" sx={{ mb: 2.5 }}>
              You are on a break
            </Typography>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Round: {round}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <IconButton
                disabled={!canMoveBack}
                onClick={() => handleClickMove("back")}
                sx={{ border: "1px solid", borderColor: "grey.300" }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                disabled={!canMoveForward}
                onClick={() => handleClickMove("next")}
                sx={{ border: "1px solid", borderColor: "grey.300" }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 2,
              }}
            >
              <IconButton
                disabled={!canMoveBack}
                onClick={() => handleClickMove("back")}
                sx={{
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  border: "2px solid",
                  borderColor: "primary.main",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: "primary.main",
                    lineHeight: 1,
                  }}
                >
                  Round
                </Typography>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "primary.main",
                    lineHeight: 1.1,
                  }}
                >
                  {roundData?.round}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                head to{" "}
                <Box component="span" sx={{ color: "primary.main" }}>
                  table {roundData?.table}
                </Box>
              </Typography>
              <IconButton
                disabled={!canMoveForward}
                onClick={() => handleClickMove("next")}
                sx={{
                  flexShrink: 0,
                  ml: "auto",
                  border: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
            {renderAttendees()}
          </>
        )}
      </Box>
      <div style={{ height: 100 }} />
    </>
  );
};

export default GroupEvent;
